import { workspaceState } from "../core/state.svelte.js";
import { client } from "./client.js";
import { type SendSurface, setBubbleStatus, surfaceFor } from "./send-bubbles.js";
import { type SendWatch, reportSendUnconfirmed } from "./send-unconfirmed.js";

// No echo for an optimistic row within the window: it turns "failed" and offers Retry.
export const SEND_WATCHDOG_MS = 12_000;
const sendWatchdogs = new Map<string, SendWatch>();

export function watchOf(localId: string): SendWatch | undefined {
  return sendWatchdogs.get(localId);
}

export function markSendWatchdogsDropped(): void {
  for (const watch of sendWatchdogs.values()) watch.connected = false;
}

export function armSendWatchdog(
  localId: string,
  surface: SendSurface,
  targetId: string,
  windowMs = SEND_WATCHDOG_MS,
): void {
  const existing = sendWatchdogs.get(localId);
  if (existing) clearTimeout(existing.timer);
  const watch: SendWatch = {
    surface,
    targetId,
    workspaceId: workspaceState.current?.id,
    connected: client.authenticated,
    timer: setTimeout(() => {
      sendWatchdogs.delete(localId);
      // The server never echoed this send: only this client knows it failed.
      reportSendUnconfirmed({
        ...watch,
        windowMs,
        localId,
        connectedThroughout: watch.connected && client.authenticated,
      });
      // A reconciled row has a server id, so this no-ops on it.
      setBubbleStatus(surface, localId, "failed");
    }, windowMs),
  };
  sendWatchdogs.set(localId, watch);
}

export function clearSendWatchdog(localId: string): void {
  const t = sendWatchdogs.get(localId);
  if (t) clearTimeout(t.timer);
  sendWatchdogs.delete(localId);
}

// Legacy net for MY echo without a clientMsgId: a local id gone from the echoing
// conversation's list was reconciled by fromId+text, so that send landed.
export function sweepReconciledWatchdogs(
  surface: SendSurface,
  targetId: string | null | undefined,
  clientMsgId: string | null | undefined,
): void {
  if (clientMsgId || sendWatchdogs.size === 0 || !targetId) return; // an echo with an id settles its own watch
  const live = new Set(surfaceFor(surface).messages.map((m) => m.id));
  for (const [localId, entry] of sendWatchdogs.entries()) {
    const own = entry.surface === surface && entry.targetId === targetId;
    if (own && !live.has(localId)) clearSendWatchdog(localId);
  }
}
